import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import Logo from "./Logo";
import { BOOK_ON_DEMAND_URL } from "../config/links";
import { useTour } from "../context/TourProvider";
import DesktopNav from "./header/DesktopNav";
import MobileDrawer from "./header/MobileDrawer";

const NAV_ITEMS = [
  { label: "Locations", to: "/locations", menu: "locations" },
  { label: "Memberships", to: "/memberships", menu: "memberships" },
  { label: "Amenities", to: "/amenities", menu: null },
  { label: "About", to: "/about", menu: null },
  { label: "Contact", to: "/contact", menu: null },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hovered, setHovered] = useState(null);
  const loc = useLocation();
  const { openTour } = useTour();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  // lock body scroll while mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/95 backdrop-blur-md border-b border-line shadow-[0_2px_20px_-12px_rgba(0,0,0,0.15)]" : "bg-white border-b border-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 flex items-center justify-between h-[72px]">
          {/* Left: logo */}
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden w-10 h-10 rounded-full border border-line flex items-center justify-center text-ink hover:bg-sand transition-colors"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-5 h-5" />
            </button>
            <Logo />
          </div>

          {/* Center: nav */}
          <DesktopNav navItems={NAV_ITEMS} hovered={hovered} setHovered={setHovered} />

          {/* Right: CTAs */}
          <div className="flex items-center gap-2.5">
            <a
              href={BOOK_ON_DEMAND_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 sm:px-6 py-2.5 rounded-full bg-camel text-white label-caps text-[10px] sm:text-[11px] btn-press hover:bg-camel-dark shadow-[0_6px_18px_-8px_rgba(176,135,98,0.8)]"
            >
              Book On-Demand
            </a>
            <button
              onClick={() => openTour()}
              data-testid="header-book-tour-btn"
              className="hidden sm:inline-flex items-center px-5 md:px-6 py-2.5 rounded-full border border-ink text-ink label-caps text-[11px] btn-press hover:bg-ink hover:text-white"
            >
              Book a Tour
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer — outside <header> so backdrop-blur doesn't trap the fixed overlay */}
      <MobileDrawer open={open} setOpen={setOpen} navItems={NAV_ITEMS} openTour={openTour} />
    </>
  );
};

export default Header;
